import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton, Chip, Tooltip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Menu, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, useTheme, useMediaQuery,
} from '@mui/material';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SnoozeRoundedIcon from '@mui/icons-material/SnoozeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { auth } from '../firebase';

const API_URL = '/api';

const TIPOS = [
  { value: 'custom',         label: 'Nota',           icon: <StickyNote2RoundedIcon />, color: '#6366f1' },
  { value: 'lab_pendiente',  label: 'Lab por revisar', icon: <ScienceRoundedIcon />,    color: '#0ea5e9' },
  { value: 'seguimiento',    label: 'Follow-up',       icon: <EventRepeatRoundedIcon />,color: '#10b981' },
  { value: 'receta_renovar', label: 'Renovar receta',  icon: <MedicationRoundedIcon />, color: '#f59e0b' },
  { value: 'cita_confirmar', label: 'Confirmar cita',  icon: <EventAvailableRoundedIcon />, color: '#8b5cf6' },
];

const PRIORIDADES = [
  { value: 'alta',  label: 'Alta',  color: '#ef4444' },
  { value: 'media', label: 'Media', color: '#f59e0b' },
  { value: 'baja',  label: 'Baja',  color: '#64748b' },
];

const FILTROS = [
  { value: 'todas',     label: 'Todas',     descripcion: 'Sin filtro de vencimiento' },
  { value: 'hoy',       label: 'Hoy',       descripcion: 'Vencen hoy' },
  { value: 'semana',    label: 'Esta semana', descripcion: 'Próximos 7 días' },
  { value: 'vencidas',  label: 'Vencidas',  descripcion: 'Pasaron la fecha' },
  { value: 'sin_fecha', label: 'Sin fecha', descripcion: 'No tienen vencimiento' },
];

function tipoMeta(tipo) {
  return TIPOS.find((t) => t.value === tipo) || TIPOS[0];
}
function prioridadMeta(p) {
  return PRIORIDADES.find((x) => x.value === p) || PRIORIDADES[1];
}

// Avatar determinístico por nombre — mismo patrón que Pacientes/Conversaciones
const AVATAR_PALETTE = [
  ['#0ea5e9', '#0284c7'], ['#8b5cf6', '#7c3aed'], ['#10b981', '#059669'],
  ['#f59e0b', '#d97706'], ['#ec4899', '#db2777'], ['#06b6d4', '#0891b2'],
  ['#6366f1', '#4f46e5'], ['#14b8a6', '#0d9488'],
];
function hashColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}
function iniciales(nombre = '') {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

function formatVencimiento(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { texto: `Hace ${Math.abs(diffDays)}d`, vencida: true };
  if (diffDays === 0) return { texto: 'Hoy', vencida: false, urgente: true };
  if (diffDays === 1) return { texto: 'Mañana', vencida: false };
  if (diffDays < 7) return { texto: `En ${diffDays}d`, vencida: false };
  return { texto: d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }), vencida: false };
}

export default function Inbox() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [userId, setUserId] = useState(localStorage.getItem('user_id'));
  const [tareas, setTareas] = useState([]);
  const [contador, setContador] = useState({ pendientes: 0, alta: 0, vencidas: 0 });
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState('pendiente'); // pendiente | completada | todas
  const [vence, setVence] = useState('todas');
  const [snack, setSnack] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [form, setForm] = useState({
    titulo: '', descripcion: '', tipo: 'custom', prioridad: 'media',
    fecha_vencimiento: '',
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) { navigate('/login'); return; }
      if (!userId) {
        try {
          const token = await u.getIdToken();
          const r = await fetch(`${API_URL}/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) {
            const data = await r.json();
            if (data.id) { localStorage.setItem('user_id', data.id); setUserId(data.id); }
          }
        } catch (e) { /* ignored */ }
      }
    });
    return () => unsub();
  }, [navigate, userId]);

  const getHeaders = useCallback(() => (
    userId ? { 'X-User-Id': String(userId), 'Content-Type': 'application/json' } : {}
  ), [userId]);

  const fetchTareas = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ estado, limit: '200' });
      if (vence !== 'todas') params.append('vence', vence);
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/tareas?${params.toString()}`, { headers: getHeaders() }),
        fetch(`${API_URL}/tareas/contador`, { headers: getHeaders() }),
        fetch(`${API_URL}/tareas/digest`, { headers: getHeaders() }),
      ]);
      if (r1.ok) setTareas(await r1.json());
      if (r2.ok) setContador(await r2.json());
      if (r3.ok) setDigest(await r3.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId, getHeaders, estado, vence]);

  useEffect(() => { fetchTareas(); }, [fetchTareas]);

  // Abrir modal con ?new=true (deep link desde dashboard)
  useEffect(() => {
    if (searchParams.get('new') === 'true' && userId) {
      abrirModalCrear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const abrirModalCrear = () => {
    setEditingTarea(null);
    setForm({ titulo: '', descripcion: '', tipo: 'custom', prioridad: 'media', fecha_vencimiento: '' });
    setDialogOpen(true);
  };

  const abrirModalEditar = (tarea) => {
    setEditingTarea(tarea);
    setForm({
      titulo: tarea.titulo,
      descripcion: tarea.descripcion || '',
      tipo: tarea.tipo,
      prioridad: tarea.prioridad,
      fecha_vencimiento: tarea.fecha_vencimiento ? tarea.fecha_vencimiento.substring(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const handleGuardar = async () => {
    if (!form.titulo.trim()) {
      setSnack({ severity: 'warning', msg: 'El título es obligatorio' });
      return;
    }
    const payload = {
      titulo: form.titulo.trim(),
      descripcion: form.descripcion || null,
      tipo: form.tipo,
      prioridad: form.prioridad,
      fecha_vencimiento: form.fecha_vencimiento || null,
    };
    try {
      const url = editingTarea
        ? `${API_URL}/tareas/${editingTarea.id}`
        : `${API_URL}/tareas`;
      const method = editingTarea ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      setDialogOpen(false);
      setSnack({ severity: 'success', msg: editingTarea ? 'Tarea actualizada' : 'Tarea creada' });
      fetchTareas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'No se pudo guardar' });
    }
  };

  const handleCompletar = async (tarea) => {
    const nuevoEstado = tarea.estado === 'completada' ? 'pendiente' : 'completada';
    try {
      const r = await fetch(`${API_URL}/tareas/${tarea.id}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (!r.ok) throw new Error();
      fetchTareas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'No se pudo actualizar' });
    }
  };

  const handleSnooze = async (tarea, dias) => {
    try {
      const r = await fetch(`${API_URL}/tareas/${tarea.id}/snooze?dias=${dias}`, {
        method: 'PATCH',
        headers: getHeaders(),
      });
      if (!r.ok) throw new Error();
      setSnack({ severity: 'success', msg: `Pospuesta ${dias} día${dias > 1 ? 's' : ''}` });
      fetchTareas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'No se pudo posponer' });
    }
  };

  const handleEliminar = async (tarea) => {
    if (!window.confirm(`¿Eliminar la tarea "${tarea.titulo}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/tareas/${tarea.id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!r.ok) throw new Error();
      setSnack({ severity: 'success', msg: 'Tarea eliminada' });
      fetchTareas();
    } catch (e) {
      setSnack({ severity: 'error', msg: 'No se pudo eliminar' });
    }
  };

  const tareasFiltradas = useMemo(() => tareas, [tareas]);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundImage: `
        radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.08) 0%, transparent 40%)
      `,
      pb: 6,
    }}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
          boxShadow: '0 8px 24px rgba(30, 58, 95, 0.20)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: -30, right: -30, width: 160, height: 160,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(251,191,36,0.18), transparent 70%)',
          }}/>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', flexWrap: 'wrap', rowGap: 1.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 3,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.22)',
            }}>
              <InboxRoundedIcon sx={{ color: '#fbbf24', fontSize: 26 }}/>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, letterSpacing: -0.3 }}>
                Inbox
              </Typography>
              <Typography sx={{ opacity: 0.78, fontSize: 13, fontWeight: 500 }}>
                Tus pendientes en un solo lugar
              </Typography>
            </Box>

            {/* Stats inline */}
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <Stat label="Pendientes" valor={contador.pendientes} color="#fff" bg="rgba(255,255,255,0.14)" />
              <Stat label="Urgentes" valor={contador.alta} color="#fff" bg="rgba(239, 68, 68, 0.30)" />
              <Stat label="Vencidas" valor={contador.vencidas} color="#fff" bg="rgba(251, 146, 60, 0.30)" />
            </Box>
          </Box>
        </Box>

        {/* Toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5,
          flexWrap: 'wrap',
        }}>
          {/* Tabs estado */}
          <Box sx={{
            display: 'flex', gap: 0, borderRadius: 3, p: 0.5,
            backgroundColor: '#fff',
            border: '1px solid rgba(226, 232, 240, 0.85)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
          }}>
            {[
              { value: 'pendiente',  label: 'Pendientes' },
              { value: 'completada', label: 'Completadas' },
              { value: 'todas',      label: 'Todas' },
            ].map((opt) => {
              const active = estado === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setEstado(opt.value)}
                  sx={{
                    px: 2, py: 0.9, borderRadius: 2.5,
                    fontSize: 12.5, fontWeight: active ? 700 : 600,
                    color: active ? '#fff' : '#64748b',
                    background: active ? 'linear-gradient(135deg, #1e3a5f, #2d5a87)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    '&:hover': { color: active ? '#fff' : '#1e3a5f' },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>

          {/* Filtro vencimiento */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={vence}
              onChange={(e) => setVence(e.target.value)}
              sx={{
                borderRadius: 2.5, backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226,232,240,0.85)' },
                fontSize: 13, fontWeight: 600,
              }}
            >
              {FILTROS.map((f) => (
                <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13 }}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={abrirModalCrear}
            sx={{
              borderRadius: 2.5, textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
              boxShadow: '0 4px 12px rgba(30, 58, 95, 0.30)',
              px: 2.5, py: 1,
              '&:hover': { background: 'linear-gradient(135deg, #1e3a5f, #1e4670)' },
            }}
          >
            Nueva tarea
          </Button>
        </Box>

        {/* Digest matutino — solo si hay actividad relevante */}
        {!loading && estado === 'pendiente' && digest && (
          (digest.vencen_hoy + digest.vencidas + digest.urgentes + (digest.auto_generadas_hoy || 0)) > 0
        ) && <DigestBanner digest={digest} />}

        {/* Lista */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tareasFiltradas.length === 0 ? (
          <EmptyState estado={estado} onCrear={abrirModalCrear} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {tareasFiltradas.map((t) => (
              <TareaCard
                key={t.id}
                tarea={t}
                onCompletar={() => handleCompletar(t)}
                onEditar={() => abrirModalEditar(t)}
                onEliminar={() => handleEliminar(t)}
                onSnooze={(dias) => handleSnooze(t, dias)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Dialog crear/editar */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
          color: '#fff', p: 2.5,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2.5,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <StickyNote2RoundedIcon sx={{ fontSize: 20 }}/>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17 }}>
              {editingTarea ? 'Editar tarea' : 'Nueva tarea'}
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
              {editingTarea ? 'Modifica los datos' : 'Agrega un pendiente a tu Inbox'}
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Título" required autoFocus fullWidth
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              size="small"
              placeholder="Ej: Revisar resultados de Greys Fernández"
            />
            <TextField
              label="Descripción (opcional)" multiline rows={3} fullWidth
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              size="small"
              placeholder="Notas, contexto, etc."
            />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo" value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                >
                  {TIPOS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: t.color, display: 'flex', '& svg': { fontSize: 18 } }}>{t.icon}</Box>
                        {t.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 130 }}>
                <InputLabel>Prioridad</InputLabel>
                <Select
                  label="Prioridad" value={form.prioridad}
                  onChange={(e) => setForm({ ...form, prioridad: e.target.value })}
                >
                  {PRIORIDADES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: p.color }}/>
                        {p.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              type="datetime-local"
              label="Vencimiento (opcional)"
              InputLabelProps={{ shrink: true }}
              size="small" fullWidth
              value={form.fecha_vencimiento}
              onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleGuardar}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
              boxShadow: '0 4px 12px rgba(30, 58, 95, 0.30)',
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #1e3a5f, #1e4670)' },
            }}
          >
            {editingTarea ? 'Guardar cambios' : 'Crear tarea'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack} autoHideDuration={2200}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

function Stat({ label, valor, color, bg }) {
  return (
    <Box sx={{
      px: 1.6, py: 0.9, borderRadius: 2.5, background: bg,
      display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5, lineHeight: 1 }}>
        {valor}
      </Typography>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.85 }}>
        {label}
      </Typography>
    </Box>
  );
}

function TareaCard({ tarea, onCompletar, onEditar, onEliminar, onSnooze }) {
  const tipo = tipoMeta(tarea.tipo);
  const prio = prioridadMeta(tarea.prioridad);
  const venc = formatVencimiento(tarea.fecha_vencimiento);
  const completada = tarea.estado === 'completada';
  const [snoozeAnchor, setSnoozeAnchor] = React.useState(null);
  const esAuto = tarea.origen && tarea.origen !== 'manual';

  return (
    <Paper elevation={0} sx={{
      p: 2,
      borderRadius: 3,
      background: '#fff',
      border: `1px solid ${venc?.vencida && !completada ? 'rgba(239,68,68,0.45)' : 'rgba(226,232,240,0.85)'}`,
      boxShadow: venc?.vencida && !completada
        ? '0 4px 14px rgba(239, 68, 68, 0.12)'
        : '0 2px 6px rgba(0,0,0,0.04)',
      opacity: completada ? 0.65 : 1,
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
        transform: 'translateY(-1px)',
      },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: 4, background: prio.color,
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 1 }}>
        {/* Checkbox grande */}
        <IconButton onClick={onCompletar} size="small" sx={{ mt: -0.5 }}>
          {completada
            ? <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: 24 }} />
            : <CheckCircleOutlineRoundedIcon sx={{ color: '#cbd5e1', fontSize: 24, '&:hover': { color: '#10b981' } }} />
          }
        </IconButton>

        {/* Cuerpo */}
        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onEditar}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: 1.2,
              background: tipo.color + '18',
              color: tipo.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '& svg': { fontSize: 14 },
            }}>
              {tipo.icon}
            </Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: tipo.color, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {tipo.label}
            </Typography>
            {tarea.paciente && (() => {
              const [c1, c2] = hashColor(tarea.paciente.nombre_completo);
              return (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.6,
                  pl: 0.4, pr: 1, py: 0.2,
                  borderRadius: 5,
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${c1}, ${c2})`,
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800, letterSpacing: 0.2,
                  }}>
                    {iniciales(tarea.paciente.nombre_completo)}
                  </Box>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#475569' }}>
                    {tarea.paciente.nombre_completo}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
          <Typography sx={{
            fontSize: 14.5,
            fontWeight: 700,
            color: '#0f172a',
            lineHeight: 1.35,
            letterSpacing: -0.1,
            textDecoration: completada ? 'line-through' : 'none',
            textDecorationColor: '#cbd5e1',
          }}>
            {tarea.titulo}
          </Typography>
          {tarea.descripcion && (
            <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: 0.5, lineHeight: 1.45 }}>
              {tarea.descripcion}
            </Typography>
          )}
          {venc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.85 }}>
              {venc.vencida
                ? <WarningAmberRoundedIcon sx={{ fontSize: 13, color: '#ef4444' }}/>
                : <ScheduleRoundedIcon sx={{ fontSize: 13, color: venc.urgente ? '#f59e0b' : '#94a3b8' }}/>
              }
              <Typography sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color: venc.vencida ? '#ef4444' : (venc.urgente ? '#f59e0b' : '#94a3b8'),
                letterSpacing: 0.2,
              }}>
                {venc.texto}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {esAuto && (
            <Tooltip title="Generada automáticamente por NARA">
              <Chip
                size="small"
                icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 12, color: '#fbbf24 !important' }} />}
                label="Auto"
                sx={{
                  height: 20, fontSize: 9.5, fontWeight: 800,
                  backgroundColor: '#1e3a5f', color: '#fff',
                  '& .MuiChip-icon': { ml: 0.4 },
                  border: 'none',
                  display: { xs: 'none', sm: 'inline-flex' },
                  letterSpacing: 0.4,
                }}
              />
            </Tooltip>
          )}
          <Chip
            size="small"
            label={prio.label}
            sx={{
              height: 22, fontSize: 10.5, fontWeight: 700,
              backgroundColor: prio.color + '18', color: prio.color,
              border: 'none',
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          />
          {!completada && onSnooze && (
            <>
              <Tooltip title="Posponer">
                <IconButton
                  onClick={(e) => { e.stopPropagation(); setSnoozeAnchor(e.currentTarget); }}
                  size="small"
                  sx={{
                    color: '#94a3b8',
                    '&:hover': { color: '#1e3a5f', backgroundColor: 'rgba(30,58,95,0.08)' },
                  }}
                >
                  <SnoozeRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={snoozeAnchor}
                open={Boolean(snoozeAnchor)}
                onClose={() => setSnoozeAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 140 } }}
              >
                {[
                  { label: '1 día',  dias: 1 },
                  { label: '3 días', dias: 3 },
                  { label: '1 semana', dias: 7 },
                  { label: '2 semanas', dias: 14 },
                ].map((opt) => (
                  <MenuItem
                    key={opt.dias}
                    onClick={() => { onSnooze(opt.dias); setSnoozeAnchor(null); }}
                    sx={{ fontSize: 12.5, fontWeight: 600 }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Tooltip title="Eliminar">
            <IconButton onClick={onEliminar} size="small" sx={{
              color: '#94a3b8',
              '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)' },
            }}>
              <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }}/>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}

function DigestBanner({ digest }) {
  const items = [
    digest.vencidas > 0     && { label: 'vencidas',      n: digest.vencidas,     color: '#ef4444' },
    digest.vencen_hoy > 0   && { label: 'vencen hoy',    n: digest.vencen_hoy,   color: '#f59e0b' },
    digest.urgentes > 0     && { label: 'urgentes',      n: digest.urgentes,     color: '#1e3a5f' },
    digest.auto_generadas_hoy > 0 && { label: 'auto-generadas hoy', n: digest.auto_generadas_hoy, color: '#fbbf24' },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 2, borderRadius: 3,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: '4px solid #1e3a5f',
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Briefing del día
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
        {items.map((it) => (
          <Box key={it.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: it.color, lineHeight: 1 }}>
              {it.n}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              {it.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}


function EmptyState({ estado, onCrear }) {
  const msg = {
    pendiente: { titulo: '¡Inbox al día!', sub: 'No tienes pendientes. Buen momento para descansar.', cta: 'Crear primera tarea' },
    completada: { titulo: 'Sin completadas', sub: 'Cuando completes tareas las verás aquí.', cta: null },
    todas: { titulo: 'Inbox vacío', sub: 'Empieza creando tu primera tarea.', cta: 'Crear tarea' },
  }[estado] || { titulo: 'Sin tareas', sub: '', cta: null };

  return (
    <Paper elevation={0} sx={{
      py: 6, px: 4, borderRadius: 4, textAlign: 'center',
      background: '#fff', border: '1px solid rgba(226,232,240,0.85)',
    }}>
      <Box sx={{
        width: 80, height: 80, borderRadius: 3, mx: 'auto', mb: 2.5,
        background: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.05))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 42, color: '#10b981' }}/>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>{msg.titulo}</Typography>
      <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5, mb: msg.cta ? 2.5 : 0 }}>
        {msg.sub}
      </Typography>
      {msg.cta && (
        <Button
          variant="contained" startIcon={<AddRoundedIcon />}
          onClick={onCrear}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2.5,
            background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
            boxShadow: '0 4px 12px rgba(30, 58, 95, 0.30)',
            px: 3, py: 1,
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f, #1e4670)' },
          }}
        >
          {msg.cta}
        </Button>
      )}
    </Paper>
  );
}
